import type { Coordinates3D } from '@wms/domain';
import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { store } from '../../session';
import { Icon } from '../icons';
import {
  DEFAULT_VIEW,
  PLAN_PITCH,
  UNIT,
  blend,
  boxes as layoutBoxes,
  floorGrid,
  framed,
  isoView,
  orbited,
  panned,
  planView,
  projector,
  scaleBar,
  screenOf,
  toPoints,
  turned,
  zoomed,
  type Box,
  type Camera,
  type Cell,
  type Margins,
  type P2,
  type Size,
  type World,
} from './geometry';

/** How a shelf looks: a colour (its sides are drawn darker), extra classes, a tooltip. */
export interface CellPaint {
  readonly fill?: string;
  readonly className?: string;
  readonly title?: string;
}

/** A line on the floor (the pick route), in world coordinates. */
export interface MapPath {
  readonly points: readonly Coordinates3D[];
  readonly className: string;
}

/** A button that frames a set of points, e.g. the stops of the route. */
export interface MapFrame {
  readonly label: string;
  readonly title: string;
  readonly points: readonly Coordinates3D[];
}

/** What a tap landed on: a shelf, or an overlay marker carrying data-hit="pin:<id>". */
export type MapHit =
  | { readonly kind: 'cell'; readonly cell: Cell }
  | { readonly kind: 'pin'; readonly id: string }
  /** a building on the floor (the office) */
  | { readonly kind: 'building'; readonly id: string };

/** Things a press can pick up and move over the floor instead of turning the map. */
export interface MapDrag {
  readonly can: (hit: MapHit | null) => boolean;
  /** the floor offset in metres since the press; `done` once the finger or button lets go */
  readonly move: (hit: MapHit, offset: { readonly x: number; readonly y: number }, done: boolean) => void;
}

export interface MapViewProps {
  readonly world: World;
  readonly label: string;
  /** colour and classes of a shelf, or of a whole rack position on the floor plan */
  readonly paint?: (cell: Cell) => CellPaint | undefined;
  readonly paths?: readonly MapPath[];
  /** markers drawn over the map at a fixed size; `at` turns a world point into pixels */
  readonly overlay?: (at: (p: Coordinates3D) => P2) => ReactNode;
  /** what the first view shows (the whole warehouse when empty); framed again when `frameKey` changes */
  readonly frame?: readonly Coordinates3D[];
  readonly frameKey?: string;
  readonly frames?: readonly MapFrame[];
  /** "Hedefe odaklan": the point the button flies to; the map follows it while zoomed in */
  readonly focus?: Coordinates3D | null;
  /** where pick routes start and end: drawn as P, a tap explains it */
  readonly depot?: Coordinates3D | null;
  readonly onTap?: (hit: MapHit | null) => void;
  readonly drag?: MapDrag;
  /** metre grid and scale bar on at first (the choice is remembered afterwards) */
  readonly measureByDefault?: boolean;
  readonly legend?: ReactNode;
  /** a card over the bottom of the map, e.g. what was tapped */
  readonly card?: ReactNode;
}

interface Limits {
  readonly min: number;
  readonly max: number;
  /** the whole warehouse at the default angle */
  readonly all: number;
}

interface Gesture {
  readonly pointers: Map<number, P2>;
  mode: 'orbit' | 'pan' | 'drag';
  moved: number;
  hit: MapHit | null;
  readonly button: number;
  readonly t0: number;
  /** where a drag started, pixels */
  readonly start: P2;
  /** the camera when the press started: drag offsets are measured with it */
  readonly cam: Camera | null;
  dragging: boolean;
}

const MEASURE_KEY = 'wms.map.measure';
const reducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
const coarsePointer = () => window.matchMedia?.('(pointer: coarse)').matches ?? false;
const isStorage = (cell: Cell) => cell.type === 'pick' || cell.type === 'bulk';

/** The sides of a shelf: rack colours shaded by how far they look away from the light, or its paint darkened. */
function sideFill(fill: string | undefined, shade: number): string {
  return fill
    ? `color-mix(in srgb, ${fill} ${84 - Math.round(shade * 14)}%, #000)`
    : `color-mix(in srgb, var(--rack-right) ${Math.round(shade * 100)}%, var(--rack-left))`;
}

/** An area's name as large as its floor allows (drawing units), so neighbouring areas' names do not run into each other. */
function areaFont(cell: Cell): number {
  const room = Math.max(cell.x1 - cell.x0, cell.y1 - cell.y0) * UNIT * 0.9;
  return Math.max(2.5, Math.min(8, room / (Math.max(4, cell.label.length) * 0.62)));
}

/** Shelves, the metre grid and route lines; redrawn only when the angle or the colours change, not on pan and zoom. */
const Scene = memo(function Scene(props: {
  boxes: readonly Box[];
  paints: readonly (CellPaint | undefined)[];
  lines: readonly { readonly className: string; readonly points: string }[];
  grid: readonly { readonly major: boolean; readonly points: string }[];
}) {
  return (
    <>
      {props.grid.map((l, i) => (
        <polyline key={`g${i}`} points={l.points} className={`map-grid${l.major ? ' major' : ''}`} />
      ))}
      {props.boxes.map((b, i) => {
        const paint = props.paints[i];
        const area = !isStorage(b.cell);
        return (
          <g
            key={b.cell.key}
            data-hit={b.cell.building ? `building:${b.cell.building}` : `cell:${b.cell.key}`}
            className={`map-cell kind-${b.cell.kind} type-${b.cell.type}${area ? ' area' : ''}${b.cell.blocked ? ' blocked' : ''}${paint?.className ? ` ${paint.className}` : ''}`}
          >
            <title>{paint?.title ?? `${b.cell.label}${b.cell.blocked ? ' · sayım için kilitli' : ''}`}</title>
            {b.sides.map((f, k) => (
              <polygon key={k} points={f.points} className="face side" style={{ fill: sideFill(paint?.fill, f.shade) }} />
            ))}
            <polygon points={b.top} className="face top" style={paint?.fill ? { fill: paint.fill } : undefined} />
            {area && (
              <text x={b.topCenter.x} y={b.topCenter.y + 3} className="map-area-label" style={{ fontSize: areaFont(b.cell) }}>
                {b.cell.label}
              </text>
            )}
          </g>
        );
      })}
      {props.lines.map((l, i) => (
        <polyline key={i} points={l.points} className={`map-route ${l.className}`} />
      ))}
    </>
  );
});

const HELP: readonly (readonly [ReactNode, string])[] = [
  [<b key="p" className="map-help-p">P</b>, 'Rota başlangıcı: toplama rotaları buradan başlar ve buraya döner (paketleme masaları). Yeri Depo tanımı → Harita’dan değişir.'],
  [<b key="a">A, B…</b>, 'Sıra harfleri, sıranın ilk rafının önünde yazar.'],
  [<b key="n">1, 2, 3…</b>, 'Raf numaraları, yakınlaşınca rafların üstünde görünür. A-03-2-1 = A sırası, 3. raf, 2. kat, 1. göz.'],
  [<Icon key="r" name="refresh" size={16} />, 'Haritayı 90° döndürür.'],
  [<span key="c" className="map-help-compass">◆</span>, 'Pusula: varsayılan açıya döner.'],
  [<b key="u">Üstten / 3B</b>, 'Kat planı (yukarıdan) ile 3B görünüm arasında geçer.'],
  [<Icon key="m" name="ruler" size={16} />, 'Ölçek: metre ızgarasını ve ölçek çubuğunu açar, kapatır.'],
  [<b key="t">Tümü</b>, 'Bütün depoyu ekrana sığdırır.'],
];

/**
 * The warehouse in 2.5D with a free camera: drag to turn it around and tilt it, right-drag,
 * Shift-drag or two fingers to move it, wheel or pinch to zoom, twist two fingers to turn.
 * Shelves are SVG polygons in drawing units under one transform; markers and labels are drawn
 * on top at a fixed size so they stay readable at any zoom.
 */
export function MapView(props: MapViewProps) {
  const { world, paint, paths, overlay, frame, frameKey, frames, focus, depot, onTap, drag, legend, card, label } = props;
  const svg = useRef<SVGSVGElement>(null);
  const tools = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState<Size | null>(null);
  const [cam, setCam] = useState<Camera | null>(null);
  const [measure, setMeasure] = useState(() => {
    const saved = store.get(MEASURE_KEY);
    return saved === null ? (props.measureByDefault ?? false) : saved === '1';
  });
  const [info, setInfo] = useState<'help' | 'depot' | null>(null);
  const camRef = useRef(cam);
  camRef.current = cam;
  const sizeRef = useRef(size);
  sizeRef.current = size;

  useLayoutEffect(() => {
    const el = svg.current;
    if (!el) return;
    const measureBox = () => {
      const r = el.getBoundingClientRect();
      if (r.width < 1 || r.height < 1) return;
      setSize((s) => (s && Math.abs(s.w - r.width) < 0.5 && Math.abs(s.h - r.height) < 0.5 ? s : { w: r.width, h: r.height }));
    };
    measureBox();
    const observer = new ResizeObserver(measureBox);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // zoom from half the whole warehouse to a shelf filling the screen
  const limits = useMemo((): Limits | null => {
    if (!size) return null;
    const all = framed(world.corners, DEFAULT_VIEW, world.center, size, { top: 0, side: 0, bottom: 0 }, { min: 1e-6, max: 1e6 }).scale;
    return { min: all * 0.45, max: Math.max(all * 14, 4), all };
  }, [world, size]);
  const limitsRef = useRef(limits);
  limitsRef.current = limits;

  // framed points are marker anchors: leave room for a badge bubble above and beside each one
  const margins = (): Margins => ({ top: (tools.current?.offsetHeight ?? 36) + 48, side: 36, bottom: 22 });

  // --- flights: buttons and the first view move the camera smoothly ---------------------
  const anim = useRef(0);
  const stopFlight = () => {
    if (anim.current) cancelAnimationFrame(anim.current);
    anim.current = 0;
  };
  const flyTo = useCallback((to: Camera, ms = 450) => {
    stopFlight();
    const from = camRef.current;
    if (!from || ms <= 0 || reducedMotion()) {
      setCam(to);
      return;
    }
    const t0 = performance.now();
    const step = (now: number) => {
      const t = Math.min(1, (now - t0) / ms);
      const eased = t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2;
      setCam(blend(from, to, eased));
      anim.current = t < 1 ? requestAnimationFrame(step) : 0;
    };
    anim.current = requestAnimationFrame(step);
  }, []);
  useEffect(() => stopFlight, []);

  /** Frames points from the current angle, no closer than three times the whole warehouse. */
  const frameTo = (points: readonly Coordinates3D[], ms?: number) => {
    if (!size || !limits) return;
    const view = camRef.current ?? DEFAULT_VIEW;
    flyTo(framed(points.length ? points : world.corners, view, world.center, size, margins(), { min: limits.min, max: limits.all * 3 }), ms);
  };

  // the first view, and again for a new route
  const framedKey = useRef<string | null>(null);
  useEffect(() => {
    if (!size || !limits) return;
    const key = frameKey ?? '';
    if (framedKey.current === key) return;
    const first = framedKey.current === null;
    framedKey.current = key;
    frameTo(frame ?? [], first ? 0 : undefined);
  });

  // a new target moves the map only while the user has zoomed in on the previous one
  const followed = useRef<string | null>(null);
  useEffect(() => {
    const key = focus ? `${focus.x}|${focus.y}|${focus.z}` : null;
    const c = camRef.current;
    if (focus && key !== followed.current && followed.current !== null && c && limits && c.scale > limits.all * 1.3) flyTo({ ...c, target: focus });
    followed.current = key;
  }, [focus, limits, flyTo]);

  // --- drawing --------------------------------------------------------------------------
  const yaw = cam?.yaw ?? DEFAULT_VIEW.yaw;
  const pitch = cam?.pitch ?? DEFAULT_VIEW.pitch;
  const proj = useMemo(() => projector({ yaw, pitch }, world.center), [yaw, pitch, world.center]);
  const plan = pitch >= PLAN_PITCH;
  const drawn = useMemo(() => layoutBoxes(plan ? world.columns : world.cells, proj), [plan, world, proj]);
  const byKey = useMemo(() => new Map(drawn.map((b) => [b.cell.key, b])), [drawn]);
  const paints = useMemo(() => drawn.map((b) => paint?.(b.cell)), [drawn, paint]);
  const lines = useMemo(() => (paths ?? []).map((p) => ({ className: p.className, points: toPoints(p.points.map(proj.project)) })), [paths, proj]);
  const grid = useMemo(() => (measure ? floorGrid(world.bounds) : null), [measure, world.bounds]);
  const gridLines = useMemo(() => (grid ? grid.lines.map((l) => ({ major: l.major, points: toPoints([proj.project(l.from), proj.project(l.to)]) })) : []), [grid, proj]);

  const ready = cam && size;
  const t = cam ? proj.project(cam.target) : { x: 0, y: 0 };
  const at = ready ? screenOf(cam, proj, size) : null;
  const pixelsPerMetre = cam ? UNIT * cam.scale : 0;

  // --- gestures -------------------------------------------------------------------------
  const gesture = useRef<Gesture | null>(null);
  const local = (clientX: number, clientY: number): P2 => {
    const r = svg.current!.getBoundingClientRect();
    return { x: clientX - r.left, y: clientY - r.top };
  };
  const hitOf = (target: EventTarget | null): MapHit | null => {
    const hit = target instanceof Element ? target.closest('[data-hit]')?.getAttribute('data-hit') : null;
    if (!hit) return null;
    if (hit.startsWith('cell:')) {
      const box = byKey.get(hit.slice(5));
      return box ? { kind: 'cell', cell: box.cell } : null;
    }
    if (hit.startsWith('building:')) return { kind: 'building', id: hit.slice(9) };
    return { kind: 'pin', id: hit.slice(4) };
  };
  const dragOffset = (g: Gesture, now: P2) => {
    const c = g.cam;
    if (!c) return { x: 0, y: 0 };
    return projector(c, world.center).unproject((now.x - g.start.x) / c.scale, (now.y - g.start.y) / c.scale);
  };
  const endGesture = (pointerId: number, timeStamp: number, tap: boolean, at?: P2) => {
    const g = gesture.current;
    if (!g) return;
    g.pointers.delete(pointerId);
    if (g.pointers.size > 0) return;
    gesture.current = null;
    if (g.mode === 'drag' && g.dragging && g.hit) {
      drag?.move(g.hit, at ? dragOffset(g, at) : { x: 0, y: 0 }, true);
      return;
    }
    if (!tap || g.button !== 0 || g.moved >= 6 || timeStamp - g.t0 >= 700) return;
    if (g.hit?.kind === 'pin' && g.hit.id === 'depot' && depot) {
      setInfo((i) => (i === 'depot' ? null : 'depot'));
      return;
    }
    setInfo(null);
    onTap?.(g.hit);
  };

  useEffect(() => {
    const el = svg.current;
    if (!el) return;
    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      const s = sizeRef.current;
      const l = limitsRef.current;
      if (!s || !l) return;
      stopFlight();
      const r = el.getBoundingClientRect();
      const pointer = { x: event.clientX - r.left, y: event.clientY - r.top };
      const unit = event.deltaMode === 1 ? 33 : event.deltaMode === 2 ? 400 : 1;
      const factor = Math.min(2, Math.max(0.5, Math.exp(-event.deltaY * unit * 0.0016)));
      setCam((c) => c && zoomed(c, world.center, s, pointer, factor, l));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [world.center]);

  // --- buttons --------------------------------------------------------------------------
  const zoomBy = (factor: number) => {
    if (!cam || !size || !limits) return;
    flyTo(zoomed(cam, world.center, size, { x: size.w / 2, y: size.h / 2 }, factor, limits), 180);
  };
  const focusNow = () => {
    if (!cam || !focus || !limits) return;
    flyTo({ ...cam, target: focus, scale: Math.min(limits.max, Math.max(cam.scale, limits.all * 2.6)) });
  };
  const toggleMeasure = () => {
    setMeasure((m) => {
      store.set(MEASURE_KEY, m ? '0' : '1');
      return !m;
    });
  };
  // the scene turns the other way round on screen than the yaw grows
  const needle = DEFAULT_VIEW.yaw - yaw;
  const bar = measure && plan && pixelsPerMetre > 0 ? scaleBar(pixelsPerMetre) : null;

  const infoCard =
    info === 'help' ? (
      <div className="map-pop map-help" role="dialog" aria-label="Harita kontrolleri">
        <div className="map-pop-h">
          <b>Harita nasıl okunur</b>
          <button className="btn sm ghost" onClick={() => setInfo(null)} aria-label="Kapat">
            <Icon name="x" size={16} />
          </button>
        </div>
        <ul>
          {HELP.map(([sign, text], i) => (
            <li key={i}>
              <span className="map-help-sign">{sign}</span>
              <span>{text}</span>
            </li>
          ))}
        </ul>
        <p className="muted">{coarsePointer() ? 'Tek parmak: döndür ve eğ · iki parmak: kaydır, yakınlaştır, çevir.' : 'Sürükle: döndür ve eğ · sağ tık veya Shift ile sürükle: kaydır · tekerlek: yakınlaştır.'}</p>
      </div>
    ) : info === 'depot' ? (
      <div className="map-pop" role="dialog" aria-label="Rota başlangıcı">
        <div className="map-pop-h">
          <span className="map-help-p">P</span>
          <b>Rota başlangıcı</b>
          <button className="btn sm ghost" onClick={() => setInfo(null)} aria-label="Kapat">
            <Icon name="x" size={16} />
          </button>
        </div>
        <p style={{ margin: '6px 0 0' }}>
          Toplama rotaları buradan başlar ve buraya döner; genelde paketleme masalarının yanıdır. Yerini Depo tanımı → Harita ekranında sürükleyerek
          değiştirebilirsiniz.
        </p>
      </div>
    ) : null;

  return (
    <div className="map-wrap">
      <div className="map-stage">
        <svg
          ref={svg}
          className={`map-svg${drag ? ' editing' : ''}`}
          viewBox={size ? `0 0 ${size.w} ${size.h}` : undefined}
          role="img"
          aria-label={label}
          onContextMenu={(e) => e.preventDefault()}
          onPointerDown={(e) => {
            if (e.pointerType === 'mouse' && e.button > 2) return;
            stopFlight();
            try {
              e.currentTarget.setPointerCapture(e.pointerId);
            } catch {
              // the pointer is already gone (lifted during a lag); the drag still works inside the map
            }
            const p = { x: e.clientX, y: e.clientY };
            const g = gesture.current;
            if (g) {
              // a second finger: never a tap, it moves, zooms and twists the map
              if (g.mode === 'drag' && g.dragging && g.hit) drag?.move(g.hit, { x: 0, y: 0 }, true);
              g.pointers.set(e.pointerId, p);
              g.hit = null;
              g.mode = 'orbit';
              g.moved += 100;
              return;
            }
            const hit = hitOf(e.target);
            const pan = e.button === 1 || e.button === 2 || e.shiftKey || e.ctrlKey || e.metaKey;
            const mode = !pan && e.button === 0 && drag?.can(hit) ? 'drag' : pan ? 'pan' : 'orbit';
            gesture.current = { pointers: new Map([[e.pointerId, p]]), mode, moved: 0, hit, button: e.button, t0: e.timeStamp, start: p, cam: camRef.current, dragging: false };
          }}
          onPointerMove={(e) => {
            const g = gesture.current;
            const before = g?.pointers.get(e.pointerId);
            if (!g || !before || !size || !limits) return;
            const now = { x: e.clientX, y: e.clientY };
            g.pointers.set(e.pointerId, now);
            if (g.pointers.size === 1) {
              const dx = now.x - before.x;
              const dy = now.y - before.y;
              g.moved += Math.abs(dx) + Math.abs(dy);
              // a tap that wobbles a little does not turn the map
              if (g.moved < 6) return;
              if (g.mode === 'drag') {
                if (g.hit) {
                  g.dragging = true;
                  drag?.move(g.hit, dragOffset(g, now), false);
                }
                return;
              }
              const mode = g.mode;
              setCam((c) => c && (mode === 'pan' ? panned(c, world.center, dx, dy) : orbited(c, dx, dy)));
              return;
            }
            const other = [...g.pointers.entries()].find(([id]) => id !== e.pointerId)?.[1];
            if (!other) return;
            const midBefore = { x: (before.x + other.x) / 2, y: (before.y + other.y) / 2 };
            const mid = local((now.x + other.x) / 2, (now.y + other.y) / 2);
            const apart = Math.hypot(before.x - other.x, before.y - other.y);
            const apartNow = Math.hypot(now.x - other.x, now.y - other.y);
            let twist = ((Math.atan2(now.y - other.y, now.x - other.x) - Math.atan2(before.y - other.y, before.x - other.x)) * 180) / Math.PI;
            if (twist > 180) twist -= 360;
            if (twist < -180) twist += 360;
            const moveX = (now.x + other.x) / 2 - midBefore.x;
            const moveY = (now.y + other.y) / 2 - midBefore.y;
            setCam((c) => {
              if (!c) return c;
              let next = panned(c, world.center, moveX, moveY);
              if (apart > 0) next = zoomed(next, world.center, size, mid, apartNow / apart, limits);
              return turned(next, world.center, size, mid, twist);
            });
          }}
          onPointerUp={(e) => endGesture(e.pointerId, e.timeStamp, true, { x: e.clientX, y: e.clientY })}
          onPointerCancel={(e) => endGesture(e.pointerId, e.timeStamp, false)}
        >
          {ready && (
            <>
              <g transform={`translate(${(size.w / 2 - t.x * cam.scale).toFixed(2)} ${(size.h / 2 - t.y * cam.scale).toFixed(2)}) scale(${cam.scale.toFixed(4)})`}>
                <Scene boxes={drawn} paints={paints} lines={lines} grid={gridLines} />
              </g>
              <g className="map-overlay">
                {grid?.ticks.map((tick) => {
                  const p = at!(tick.at);
                  return (
                    <text key={`t${tick.text}${tick.at.x}${tick.at.y}`} x={p.x} y={p.y} className="map-tick">
                      {tick.text}
                    </text>
                  );
                })}
                {world.rackLabels.map((l) => {
                  // a number only where it fits on its rack
                  if (l.room * pixelsPerMetre < 13) return null;
                  const p = at!(l.at);
                  return (
                    <text key={l.key} x={p.x} y={p.y + 4} className="map-rack-no">
                      {l.text}
                    </text>
                  );
                })}
                {world.aisleLabels.map((l) => {
                  const p = at!(l.at);
                  return (
                    <text key={l.aisle} x={p.x} y={p.y} className="map-aisle">
                      {l.aisle}
                    </text>
                  );
                })}
                {depot &&
                  (() => {
                    const p = at!({ ...depot, z: 0 });
                    return (
                      <g className="map-start map-pin" data-hit="pin:depot">
                        <rect x={p.x - 11} y={p.y - 11} width={22} height={22} rx={5} />
                        <text x={p.x} y={p.y + 4}>
                          P
                        </text>
                        <title>P · Rota başlangıcı (paketleme masaları); açıklama için dokunun</title>
                      </g>
                    );
                  })()}
                {overlay?.(at!)}
              </g>
            </>
          )}
        </svg>

        <div className="map-tools" ref={tools}>
          {focus !== undefined && (
            <button className="btn sm" onClick={focusNow} disabled={!focus} title="Şimdiki hedef göze yakınlaş">
              <Icon name="scan" size={16} />
              <span className="map-tool-text">Hedefe odaklan</span>
            </button>
          )}
          {frames?.map((f) => (
            <button key={f.label} className="btn sm" onClick={() => frameTo(f.points)} title={f.title} disabled={f.points.length === 0}>
              {f.label}
            </button>
          ))}
          <button className="btn sm" onClick={() => frameTo(world.corners)} title="Bütün depoyu göster">
            Tümü
          </button>
          <span className="map-tools-end">
            <button className={`btn sm${measure ? ' on' : ''}`} onClick={toggleMeasure} title="Metre ızgarası ve ölçek çubuğu" aria-label="Ölçek" aria-pressed={measure}>
              <Icon name="ruler" size={16} />
              <span className="map-tool-text">Ölçek</span>
            </button>
            <button className="btn sm map-rotate" onClick={() => cam && flyTo({ ...cam, yaw: cam.yaw + 90 })} title="Haritayı 90° döndür" aria-label="Haritayı 90° döndür">
              <Icon name="refresh" size={16} />
            </button>
            <button
              className="btn sm map-compass"
              onClick={() => cam && flyTo({ ...cam, ...DEFAULT_VIEW })}
              title="Varsayılan açıya dön"
              aria-label="Varsayılan açıya dön"
            >
              <svg viewBox="-12 -12 24 24" width={18} height={18} style={{ transform: `rotate(${needle}deg)` }} aria-hidden>
                <path d="M0 -10 L4.5 1 L-4.5 1 Z" className="n" />
                <path d="M0 10 L4.5 -1 L-4.5 -1 Z" className="s" />
              </svg>
            </button>
            <button className="btn sm" onClick={() => cam && flyTo({ ...cam, ...(plan ? isoView(cam) : planView(cam)) })} title={plan ? '3B görünüme geç' : 'Kat planı: yukarıdan bak'}>
              {plan ? '3B' : 'Üstten'}
            </button>
            <span className="map-zoom">
              <button className="btn sm" onClick={() => zoomBy(1.35)} aria-label="Yakınlaştır" title="Yakınlaştır">
                +
              </button>
              <button className="btn sm" onClick={() => zoomBy(1 / 1.35)} aria-label="Uzaklaştır" title="Uzaklaştır">
                −
              </button>
            </span>
            <button className={`btn sm${info === 'help' ? ' on' : ''}`} onClick={() => setInfo((i) => (i === 'help' ? null : 'help'))} title="Harita nasıl okunur" aria-label="Harita nasıl okunur">
              <Icon name="help" size={16} />
            </button>
          </span>
        </div>
        {bar && (
          <div className="map-scale" aria-label={`Ölçek: ${bar.metres} metre`}>
            <span style={{ width: bar.pixels }} />
            <b>{String(bar.metres).replace('.', ',')} m</b>
          </div>
        )}
        {measure && !plan && <div className="map-scale note">Ölçek çubuğu “Üstten” görünümde; ızgara 1 m</div>}
        {infoCard ?? card}
      </div>
      <div className="map-legend">
        {legend}
        {depot && (
          <span>
            <b className="map-help-p">P</b> Rota başlangıcı
          </span>
        )}
        <span className="map-hint">
          {coarsePointer() ? 'Tek parmak: döndür ve eğ · iki parmak: kaydır, yakınlaştır' : 'Sürükle: döndür ve eğ · sağ tık veya Shift ile sürükle: kaydır · tekerlek: yakınlaştır'}
        </span>
      </div>
    </div>
  );
}
